import type { SalesChecklist } from "@expert/contentful";
import { Accordion, Divider, Flex, ScrollArea, Text } from "@mantine/core";
import { type JSX, useMemo, useState } from "react";
import { ChecklistSection } from "@/sales-tools/home-product/components/FullOfferChecklist/components/ChecklistSection";
import { isSectionComplete } from "@/sales-tools/home-product/components/FullOfferChecklist/utils/isSectionComplete";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useSessionId } from "@/sdk";
import { LanguageSelector } from "../../../language-selector/LanguageSelector";
import { ChecklistIcon } from "../Icons";

export type FullOfferChecklistProps = {
    salesChecklist: SalesChecklist;
};

export function FullOfferChecklist({ salesChecklist }: FullOfferChecklistProps): JSX.Element | null {
    const sessionId = useSessionId();
    const firstSectionToOpen = useMemo(
        () =>
            salesChecklist.checklistSections.find(
                (section) =>
                    section.checklistItems.length !== 0 &&
                    !isSectionComplete(section.checklistItems, useOfferChecklistStore.getState().getData(sessionId)),
            )?.title,
        [salesChecklist, sessionId],
    );

    const [accordionValue, setAccordionValue] = useState<string[] | undefined>(
        firstSectionToOpen ? [firstSectionToOpen] : undefined,
    );

    const onSectionComplete = (title: string) => {
        setAccordionValue((prevState) => {
            const indexOfCompletedSection = salesChecklist.checklistSections.findIndex(
                (section) => section.title === title,
            );
            const nextTitle = salesChecklist.checklistSections.at(indexOfCompletedSection + 1)?.title;
            if (nextTitle) {
                prevState?.push(nextTitle);
            }

            if (prevState?.includes(title)) {
                prevState = prevState.filter((t) => t !== title);
            }
            return prevState;
        });
    };

    return (
        <Flex data-testid="step-two-content" direction="column" w="100%">
            <ScrollArea h="calc(100% - var(--customer-panel-bottom-control-footer-height))">
                <Flex id="full-offer-checklist-header" c="dark.2" px="xl" py="md" pb="xxs" justify="space-between">
                    <Flex align="center">
                        <ChecklistIcon />
                        <Text fw={700} fz="xs" pl="3xs">
                            OFFER CHECKLIST
                        </Text>
                    </Flex>
                    <LanguageSelector />
                </Flex>
                <Divider size="xs" color="dark.4" />
                <Accordion
                    transitionDuration={250}
                    multiple
                    value={accordionValue}
                    onChange={(section) => setAccordionValue(section)}
                >
                    {salesChecklist.checklistSections.map((section) => {
                        if (section.checklistItems.length === 0) return null;
                        return (
                            <ChecklistSection
                                onSectionComplete={onSectionComplete}
                                key={section.id}
                                section={section}
                            />
                        );
                    })}
                </Accordion>
            </ScrollArea>
        </Flex>
    );
}
